import React, { useState, useEffect } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Eye, EyeOff, CheckCircle, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { setAuthToken } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptInvite = () => {
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false });

  useEffect(() => {
    if (!token) { setError('No invitation token provided'); setLoading(false); return; }
    const load = async () => {
      try {
        const [infoRes, reqsRes] = await Promise.all([
          axios.get(`${API}/auth/invite-info?token=${token}`),
          axios.get(`${API}/auth/password-requirements`),
        ]);
        setInviteInfo(infoRes.data);
        setPasswordRequirements(reqsRes.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  useEffect(() => {
    if (password && passwordRequirements) {
      const pr = passwordRequirements;
      const ok =
        password.length >= pr.min_length &&
        (!pr.require_uppercase || /[A-Z]/.test(password)) &&
        (!pr.require_lowercase || /[a-z]/.test(password)) &&
        (!pr.require_numbers || /[0-9]/.test(password)) &&
        (!pr.require_special_chars || new RegExp(`[${pr.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password));
      setPasswordStrength({ isValid: ok });
    }
  }, [password, passwordRequirements]);

  if (user) return <Navigate to="/" replace />;

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!passwordStrength.isValid) { toast.error('Password does not meet requirements'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/accept-invite`, { token, password });
      const { token: authToken, org_id, user: userData } = res.data;
      setAuthToken(authToken);

      let perms = [];
      try {
        const permRes = await axios.get(`${API}/auth/me/permissions`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        perms = permRes.data?.permissions || [];
      } catch {}

      const userWithPerms = { ...userData, permissions: perms };
      localStorage.setItem('user', JSON.stringify(userWithPerms));

      let orgData = null;
      try {
        const orgsRes = await axios.get(`${API}/platform/orgs`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        orgData = (orgsRes.data || []).find((o) => o.org_id === org_id) || null;
      } catch {}

      login(userWithPerms, authToken, orgData);
      toast.success('Welcome! Your account is ready.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const PasswordChecklist = () => {
    if (!passwordRequirements || !password) return null;
    const pr = passwordRequirements;
    const checks = [
      { ok: password.length >= pr.min_length, label: `${pr.min_length}+ chars` },
      { ok: /[A-Z]/.test(password), label: 'Uppercase' },
      { ok: /[a-z]/.test(password), label: 'Lowercase' },
      { ok: /[0-9]/.test(password), label: 'Number' },
      { ok: new RegExp(`[${pr.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password), label: 'Special' },
    ];
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {checks.map((c) => (
          <span key={c.label} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${c.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {c.ok ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-slate-300" />}
            {c.label}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight">Shiftela</h1>
          <p className="text-slate-400 text-sm mt-1">Accept your invitation</p>
        </div>

        <Card className="border-slate-800 bg-slate-900/80 backdrop-blur-xl shadow-2xl">
          <CardHeader>
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            )}
            {error && (
              <div className="text-center py-6" data-testid="invite-error">
                <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                <p className="text-slate-300 font-medium">{error}</p>
                <Button variant="outline" className="mt-4 border-slate-700 text-slate-300" onClick={() => window.location.href = '/login'}>
                  Go to Login
                </Button>
              </div>
            )}
            {inviteInfo && !error && (
              <>
                <div className="space-y-3 mb-4">
                  <div className="bg-slate-800/60 rounded-lg p-4 text-center" data-testid="invite-details">
                    <p className="text-slate-400 text-sm">You've been invited to</p>
                    <p className="text-white text-lg font-semibold mt-1">{inviteInfo.org_name}</p>
                    <p className="text-slate-500 text-xs mt-1">by {inviteInfo.invited_by_name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-800/40 rounded-lg p-3">
                      <p className="text-slate-500 text-xs">Name</p>
                      <p className="text-slate-200 text-sm font-medium truncate">{inviteInfo.name}</p>
                    </div>
                    <div className="bg-slate-800/40 rounded-lg p-3">
                      <p className="text-slate-500 text-xs">Email</p>
                      <p className="text-slate-200 text-sm font-medium truncate">{inviteInfo.email}</p>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleAccept} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-slate-300 text-sm">Set your password</Label>
                    <div className="relative">
                      <Input
                        data-testid="invite-password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a strong password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 pr-10 focus:border-blue-500"
                      />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff className="h-4 w-4 text-slate-500" /> : <Eye className="h-4 w-4 text-slate-500" />}
                      </button>
                    </div>
                    <PasswordChecklist />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white" disabled={submitting || !passwordStrength.isValid} data-testid="accept-invite-button">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Setting up...</> : <>Join {inviteInfo.org_name}<ArrowRight className="h-4 w-4 ml-2" /></>}
                  </Button>
                </form>
              </>
            )}
          </CardHeader>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvite;
